import { ReactNode } from 'react';

import { Button } from 'antd';
import cs from 'classnames';

import { AlertType } from 'constants/enums';

import { ReactComponent as CheckCircle } from 'assets/icons/check-circle.svg';
import { ReactComponent as CloseIcon } from 'assets/icons/close.svg';
import { ReactComponent as CrownIcon } from 'assets/icons/crown-2.svg';
import { ReactComponent as DangerIcon } from 'assets/icons/danger.svg';
import { ReactComponent as InfoCircleIcon } from 'assets/icons/info-circle.svg';
import { ReactComponent as SuccessIcon } from 'assets/icons/tick-circle.svg';
import { ReactComponent as ErrorIcon } from 'assets/icons/warning.svg';

import styles from './Alert.module.scss';

interface AlertProps {
  type: AlertType;
  message?: string | ReactNode;
  title?: string;
  onClose?: () => void;
  className?: string;
  style?: React.CSSProperties;
  children?: React.ReactNode;
  closeToast?: () => void;
  actionButton?: ReactNode;
}

const ALERT_ICONS_MAPPING = {
  [AlertType.Info]: InfoCircleIcon,
  [AlertType.Warning]: DangerIcon,
  [AlertType.Error]: ErrorIcon,
  [AlertType.Success]: SuccessIcon,
  [AlertType.Premium]: CrownIcon,
  [AlertType.Check]: CheckCircle
};

const Alert = ({
  type = AlertType.Warning,
  title,
  message,
  onClose,
  className,
  style,
  children,
  closeToast,
  actionButton
}: AlertProps) => {
  const Icon = ALERT_ICONS_MAPPING[type];

  const handleClose = () => {
    if (onClose) {
      onClose();
    }

    if (closeToast) {
      closeToast();
    }
  };

  const isShowCloseIcon = onClose || closeToast;

  return (
    <div
      className={cs(styles.container, styles[type], className)}
      style={style}
    >
      <div className={`${styles.iconWrapper} ${styles[`${type}IconWrapper`]}`}>
        <Icon className={styles.icon} />
      </div>
      <div
        className={cs(styles.content, {
          [styles.withButton]: Boolean(actionButton)
        })}
      >
        <div className={styles.text}>
          {title && (
            <div className={cs(styles.title, styles[`${type}Title`])}>
              {title}
            </div>
          )}
          {message && (
            <div className={cs(styles.message, styles[`${type}Message`])}>
              {message}
            </div>
          )}
          {children && <div className={styles.message}>{children}</div>}
        </div>
        {actionButton && (
          <div className={styles.actionButton}>{actionButton}</div>
        )}
      </div>
      {isShowCloseIcon && (
        <div className={styles.closeButtonWrapper}>
          <Button
            className={styles.closeButton}
            type="text"
            icon={<CloseIcon className={styles.closeButtonIcon} />}
            onClick={handleClose}
          />
        </div>
      )}
    </div>
  );
};

export default Alert;
